import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import { withRouter } from 'react-router-dom';
import AppContext from '../../AppContext';
import { WhiteModal, FlexBox } from '../../Components';
import { useModal } from '../../utils/effects';
import { routes } from '../../routes';
import { onChange, onSelectNumber } from './actions';
import {
  startBarcodeScanner,
  stopBarcodeScanner,
  scannerStatuses,
} from './barcodeScanner';
import { startHiddenScan, stopHiddenScan } from './hiddenScanner';
import { getCurrentOrder } from '../../selectors';
import { isOrderEditable } from '../../utils/order';
import {
  modalStyles,
  ScannerNavTrigger,
  EmptyOrderState,
  ScannerSection,
  ProductSearchSection,
} from './components';
import barcodeModalStyles from './styles.module.scss';

const NUMERIC_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const EAN_LENGTH = 13;

export const BarcodeScanner = withRouter(({ history }) => {
  const { openModal, closeModal, isOpen } = useModal();
  const [scanWithHiddenModal, setScanWithHiddenModal] = useState();
  const [inputValue, setInputValue] = useState(null);
  const [scanValue, setScanValue] = useState(null);
  const [scannerStatus, setScannerStatus] = useState(scannerStatuses.idle);
  const [scanCount, setScanCount] = useState(0);
  const [validation, setValidation] = useState(null);
  const [virtualCartProductNumbers, setVirtualCartProductNumbers] = useState(
    [],
  );

  const context = useContext(AppContext);

  const handleChange = onChange({
    setInputValue,
    setValidation,
    context,
    virtualCartProductNumbers,
    setVirtualCartProductNumbers,
    history,
  });

  // Closing the modal stops the camera unless the nav "Scan" mode is using it
  useEffect(() => {
    if (!isOpen && !scanWithHiddenModal) stopBarcodeScanner();
  }, [isOpen, scanWithHiddenModal]);

  useEffect(() => {
    if (scanValue && scanValue.text) {
      handleChange(scanValue.text);
      setScanValue(null);
      setScanCount((count) => count + 1);
    }
  }, [scanValue, handleChange]);

  const currentOrder = getCurrentOrder(context);
  const isCurrentOrderEditable = isOrderEditable(_.get(currentOrder, 'doc'));

  useEffect(() => {
    if (!isOpen || !isCurrentOrderEditable) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      const input = document.querySelector('.searchBox input');
      if (input) {
        input.focus();
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [isOpen, isCurrentOrderEditable]);

  const selectProduct = onSelectNumber({ context, history });

  const toggleHiddenScan = () => {
    if (scanWithHiddenModal) {
      setScanWithHiddenModal(false);
      stopHiddenScan();
    } else {
      setScanWithHiddenModal(true);
      startHiddenScan(setScanValue, setScannerStatus);
    }
  };

  const startCameraScan = () => startBarcodeScanner(setScanValue, setScannerStatus);

  const handleNumericKey = (key) => {
    const nextValue = ((inputValue || '') + key).slice(-EAN_LENGTH);
    setInputValue(nextValue);
    handleChange(nextValue);
  };

  const goToCustomerSelection = () => {
    closeModal();
    history.push(routes.orderCustomerSelection);
  };

  const isKeyboardListenerActive = (scanWithHiddenModal || isOpen)
    && isCurrentOrderEditable;

  return (
    <>
      <ScannerNavTrigger
        isScanning={scanWithHiddenModal}
        scannerStatus={scannerStatus}
        onToggleHiddenScan={toggleHiddenScan}
        onOpenModal={openModal}
      />

      <div>
        {isKeyboardListenerActive && (
          <KeyboardEventHandler
            handleFocusableElements
            handleKeys={NUMERIC_KEYS}
            onKeyEvent={handleNumericKey}
          />
        )}
      </div>

      <WhiteModal style={modalStyles} isOpen={isOpen} onCancel={closeModal}>
        {!isCurrentOrderEditable && (
          <div className={barcodeModalStyles.modalBody}>
            <EmptyOrderState onGoToCustomerSelection={goToCustomerSelection} />
          </div>
        )}
        {isCurrentOrderEditable && (
          <div className={barcodeModalStyles.modalBody}>
            <FlexBox flexDirection="column" flex={1}>
              <ScannerSection
                inputValue={inputValue}
                validation={validation}
                onStartCamera={startCameraScan}
                scannerStatus={scannerStatus}
                scanCount={scanCount}
                isModalOpen={isOpen}
              />
              <ProductSearchSection onSelectProduct={selectProduct} />
            </FlexBox>
          </div>
        )}
      </WhiteModal>
    </>
  );
});
